import React from "react";

/**
 * Inline line icons for the purchase report, kept local so the report does not
 * pull a second icon set in next to MUI's.
 */
type IconProps = {
    size?: number
    color?: string
}

const Svg: React.FC<IconProps & { children: React.ReactNode }> = ({ size = 15, color = "currentColor", children }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width={size} height={size}
        fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
        style={{ flex: "0 0 auto", display: "block" }}>
        {children}
    </svg>
)

export const ScaleIcon: React.FC<IconProps> = (p) => (
    <Svg {...p}>
        <path d="M12 3v18" />
        <path d="m19 8 3 8a5 5 0 0 1-6 0zV7" />
        <path d="M3 7h1a17 17 0 0 0 8-2 17 17 0 0 0 8 2h1" />
        <path d="m5 8 3 8a5 5 0 0 1-6 0zV7" />
        <path d="M7 21h10" />
    </Svg>
)

export const TrendIcon: React.FC<IconProps> = (p) => (
    <Svg {...p}>
        <path d="M16 7h6v6" />
        <path d="m22 7-8.5 8.5-5-5L2 17" />
    </Svg>
)

export const RouteIcon: React.FC<IconProps> = (p) => (
    <Svg {...p}>
        <circle cx="6" cy="19" r="3" />
        <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
        <circle cx="18" cy="5" r="3" />
    </Svg>
)

export const PalmIcon: React.FC<IconProps> = (p) => (
    <Svg {...p}>
        <path d="M13 8c0-2.76-2.46-5-5.5-5S2 5.24 2 8h2l1-1 1 1h4" />
        <path d="M13 7.14A5.82 5.82 0 0 1 16.5 6c3.04 0 5.5 2.24 5.5 5h-3l-1-1-1 1h-3" />
        <path d="M5.89 9.71c-2.15 2.15-2.3 5.47-.35 7.43l4.24-4.25.7-.7.71-.71 2.12-2.12c-1.95-1.96-5.27-1.8-7.42.35" />
        <path d="M11 15.5c.5 2.5-.17 4.5-1 6.5h4c2-5.5-.5-12-1-14" />
    </Svg>
)

export const TableIcon: React.FC<IconProps> = (p) => (
    <Svg {...p}>
        <path d="M12 3v18" />
        <rect width="18" height="18" x="3" y="3" rx="2" />
        <path d="M3 9h18" />
        <path d="M3 15h18" />
    </Svg>
)

export const SpreadsheetIcon: React.FC<IconProps> = (p) => (
    <Svg {...p}>
        <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" />
        <path d="M14 2v5a1 1 0 0 0 1 1h5" />
        <path d="M8 13h2" /><path d="M14 13h2" /><path d="M8 17h2" /><path d="M14 17h2" />
    </Svg>
)

export const DocumentIcon: React.FC<IconProps> = (p) => (
    <Svg {...p}>
        <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" />
        <path d="M14 2v5a1 1 0 0 0 1 1h5" />
        <path d="M10 9H8" /><path d="M16 13H8" /><path d="M16 17H8" />
    </Svg>
)

export const HelpCircleIcon: React.FC<IconProps> = (p) => (
    <Svg size={13} {...p}>
        <circle cx="12" cy="12" r="10" />
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <path d="M12 17h.01" />
    </Svg>
)

export const ChevronDownIcon: React.FC<IconProps> = ({ size = 14, color = "currentColor" }) => (
    <svg className="ds-btn__chevron" width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke={color} strokeWidth={2.4} strokeLinecap="round">
        <path d="m6 9 6 6 6-6" />
    </svg>
)

/** Chevrons pointing outwards to widen the map, inwards to give the table its space back. */
export const ExpandIcon: React.FC<IconProps & { expanded?: boolean }> = ({ expanded, ...p }) => (
    <Svg {...p}>
        {expanded
            ? <><path d="M9 4v5H4" /><path d="M15 20v-5h5" /><path d="M15 9V4h5" /><path d="M9 15v5H4" /></>
            : <><path d="M4 9V4h5" /><path d="M20 15v5h-5" /><path d="M20 9V4h-5" /><path d="M4 15v5h5" /></>}
    </Svg>
)

export const ChevronRightIcon: React.FC<IconProps> = (p) => (
    <Svg {...p}>
        <path d="m9 18 6-6-6-6" />
    </Svg>
)
